import { useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import en from './en.json';
import tr from './tr.json';

// en.json / tr.json are generated from the Flutter app's
// lib/l10n/app_localizations_{en,tr}.dart (plain getters only), so the web
// shows exactly the same copy as the mobile app. Parameterized strings and
// web-only copy live below.
const extra = {
  en: {
    navNutrition: 'Nutrition',
    strengthProgressTitle: 'Strength Progress',
    strengthProgressEmpty: 'Log weights in a guided workout and your lifts will show up here.',
    strengthShowAll: 'Show all',
    strengthShowLess: 'Show less',
    liftTopSetChartTitle: 'Heaviest set',
    liftMostRepsChartTitle: 'Most reps',
    liftBestSetLabel: 'Best set',
    liftOneRepMaxLabel: 'Est. 1RM',
    liftSessionsLabel: 'Workouts',
    liftHistoryTitle: 'History',
    liftRecordTag: 'PR',
    repsUnit: 'reps',
    loading: 'Loading…',
    saving: 'Saving…',
    saved: 'Saved',
    systemTheme: 'System',
    theme: 'Theme',
    allBadges: 'All badges',
    weeklySummaryOpen: 'Open summary',
    weeklySummaryWebLocked: 'Unlock this week\'s summary in the GymGM mobile app, or upgrade to Premium to see it here.',
    getTheApp: 'Get the app',
    webOnlyMobileFeature: 'Available in the mobile app',
    homeWidgetWebHint: 'Add the GymGM widget from the mobile app.',
    exerciseDetails: 'Exercise details',
    muscleAll: 'All muscles',
    selectDay: 'Select a day',
    renameDay: 'Change day',
    dragToReorder: 'Drag to reorder',
    viewOnly: 'View only',
    snapshotProgram: 'Program snapshot',
    pickPhoto: 'Choose a photo',
    photoTooLarge: 'Photo is too large (max 8 MB).',
    textMode: 'Text',
    photoMode: 'Photo',
    manualMode: 'Manual',
    entryLabel: 'Label',
    kcal: 'kcal',
    goal: 'Goal',
    weeklyTotals: 'Weekly totals',
    addWater: 'Add water',
    customAmount: 'Custom (ml)',
    reminderTime: 'Reminder time',
    deleteAccount: 'Delete account',
    planManagedInApp: 'Subscriptions are purchased and managed in the GymGM mobile app.',
    openPlayStore: 'Google Play',
    openAppStore: 'App Store',
    programPreviewDays: 'days',
    trainerViewProgram: 'Program',
    trainerViewNutrition: 'Nutrition',
    trainerViewProgress: 'Progress',
    back: 'Back',
    noMeals: 'No meals logged.',
    previewNotAvailable: 'No preview',
    currentProgram: 'Current program',
    insightHistory: 'Past insights',
    snapshotHistory: 'Snapshots',
    landingTagline: 'Your training, nutrition and progress — in one place.',
    landingSubtitle: 'AI-built workout programs, meal analysis from a photo or a sentence, water tracking, streaks and badges. Same account as the mobile app.',
    landingFeatureProgramTitle: 'AI workout programs',
    landingFeatureProgramDesc: 'Pick your goal, level and days — get a ready program in seconds, then tweak every set and rep.',
    landingFeatureNutritionTitle: 'Nutrition AI',
    landingFeatureNutritionDesc: 'Describe a meal or upload a photo and get calories, protein, carbs and fat instantly.',
    landingFeatureStreakTitle: 'Streaks & badges',
    landingFeatureStreakDesc: 'Keep your workout and nutrition streaks alive and unlock achievements along the way.',
    landingFeatureProgressTitle: 'Progress tracking',
    landingFeatureProgressDesc: 'Weight and body-fat charts, weekly AI summaries and program insights.',
    landingFeatureWaterTitle: 'Water tracking',
    landingFeatureWaterDesc: 'Log water in one tap and hit your daily hydration goal.',
    landingFeatureTrainerTitle: 'Trainer – athlete connection',
    landingFeatureTrainerDesc: 'Trainers follow their athletes\' programs, meals and progress from one dashboard.',
    forgotPassword: 'Forgot password?',
    sendResetLink: 'Send reset link',
    resetLinkSent: 'Password reset email sent. Check your inbox.',
    backToSignIn: 'Back to sign in',
    iAmA: 'I am a',
    blog: 'Blog',
    footerRights: 'All rights reserved.',
    starterTodosAllDone: 'You have completed all starter tasks, so the list is hidden on Home.',
    promoRedeemTitle: 'Have a code?',
    promoRedeemDesc: 'Enter a code from your trainer or a GymGM promotion to unlock Premium on the web and in the app.',
    promoCodePlaceholder: 'e.g. EMRE10',
    promoApply: 'Apply',
    promoAlreadyUsed: 'You have already used this code.',
    promoAlreadyPremium: 'Your current plan already includes this.',
    promoUsedUp: 'This code has reached its usage limit.',
    promoError: 'Could not apply the code. Please try again.',
    adminPanel: 'Admin',
    adminPromoCodes: 'Promo codes',
    adminCreateCode: 'Create code',
    adminCode: 'Code',
    adminPlan: 'Plan',
    adminDurationDays: 'Premium duration (days)',
    adminMaxUses: 'Usage limit',
    adminLastRedeemDate: 'Can be used until (optional)',
    adminNote: 'Note (e.g. trainer name)',
    adminUses: 'Uses',
    adminActive: 'Active',
    adminInactive: 'Inactive',
    adminRedemptions: 'Who used it',
    adminNoCodes: 'No codes yet.',
    adminNoRedemptions: 'No one has used this code yet.',
    adminCodeCreated: 'Code created',
    adminErrExists: 'A code with this name already exists.',
    adminErrInvalidCode: 'Code must be 3–32 characters: letters, numbers, - or _.',
    adminErrInvalidNumbers: 'Check the duration (1–3650 days) and usage limit (1–100000).',
    adminErrInvalidDate: 'Invalid date.',
    adminCopied: 'Copied',
    adminDeactivate: 'Deactivate',
    adminActivate: 'Activate',
    adminDays: 'days',
    adminEndsAt: 'Premium ends',
    adminRedeemedAt: 'Used on',
    adminRefresh: 'Refresh',
    adminPressRefresh: 'Press Refresh to load.',
    adminDelete: 'Delete',
    adminDeleteTitle: 'Delete code?',
    adminDeleteMessage: 'The code stops working immediately and its usage history is removed. People who already used it keep Premium until their end date.',
    adminDeleted: 'Code deleted',
    adminLegacy: 'Legacy',
    adminFeedback: 'Feedback',
    adminFbStatus: 'Status',
    adminFbStatusNew: 'New',
    adminFbStatusRead: 'Read',
    adminFbStatusDone: 'Done',
    adminFbOpen: 'Open',
    adminFbAll: 'All',
    adminFbAllTypes: 'All types',
    adminFbAnonymous: 'Anonymous',
    adminFbEmpty: 'No feedback here.',
    adminFbMarkAllRead: 'Mark all read',
    adminFbDeleteTitle: 'Delete feedback?',
    adminFbDeleted: 'Feedback deleted',
    adminLeaderboard: 'XP ranking',
    adminLbTop: 'Show top',
    adminLbLevel: 'Level',
    adminLbEmpty: 'No one has XP yet.',
    adminTrainers: 'Trainers',
    adminTrHint: 'Accounts with at least one trainee in their list. Click a trainer to see their trainees.',
    adminTrSearch: 'Search trainer or trainee',
    adminTrEmpty: 'No trainer has a connected trainee yet.',
    adminTrNoMatch: 'No match.',
    adminTrCount: 'Trainees',
    adminTrNoTrainerEmail: 'No trainer on their profile',
    adminTrNotTrainerRole: 'Role switched to athlete',
    adminRedeemHint: 'Users redeem codes at gymgm.com → Settings. Premium then shows on Android, iOS and web.',
  },
  tr: {
    navNutrition: 'Beslenme',
    strengthProgressTitle: 'Güç İlerlemesi',
    strengthProgressEmpty: 'Rehberli antrenmanda ağırlık girdikçe hareketlerin burada görünecek.',
    strengthShowAll: 'Tümünü göster',
    strengthShowLess: 'Daha az göster',
    liftTopSetChartTitle: 'En ağır set',
    liftMostRepsChartTitle: 'En çok tekrar',
    liftBestSetLabel: 'En iyi set',
    liftOneRepMaxLabel: 'Tahmini 1RM',
    liftSessionsLabel: 'Antrenman',
    liftHistoryTitle: 'Geçmiş',
    liftRecordTag: 'Rekor',
    repsUnit: 'tekrar',
    loading: 'Yükleniyor…',
    saving: 'Kaydediliyor…',
    saved: 'Kaydedildi',
    systemTheme: 'Sistem',
    theme: 'Tema',
    allBadges: 'Tüm rozetler',
    weeklySummaryOpen: 'Özeti aç',
    weeklySummaryWebLocked: 'Bu haftanın özetini GymGM mobil uygulamasından açabilir ya da Premium\'a geçerek burada görebilirsin.',
    getTheApp: 'Uygulamayı indir',
    webOnlyMobileFeature: 'Mobil uygulamada kullanılabilir',
    homeWidgetWebHint: 'GymGM widget\'ını mobil uygulamadan ekleyebilirsin.',
    exerciseDetails: 'Egzersiz detayı',
    muscleAll: 'Tüm kaslar',
    selectDay: 'Gün seç',
    renameDay: 'Günü değiştir',
    dragToReorder: 'Sıralamak için sürükle',
    viewOnly: 'Yalnızca görüntüleme',
    snapshotProgram: 'Program anlık görüntüsü',
    pickPhoto: 'Fotoğraf seç',
    photoTooLarge: 'Fotoğraf çok büyük (en fazla 8 MB).',
    textMode: 'Metin',
    photoMode: 'Fotoğraf',
    manualMode: 'Manuel',
    entryLabel: 'Etiket',
    kcal: 'kcal',
    goal: 'Hedef',
    weeklyTotals: 'Haftalık toplam',
    addWater: 'Su ekle',
    customAmount: 'Özel (ml)',
    reminderTime: 'Hatırlatma saati',
    deleteAccount: 'Hesabı sil',
    planManagedInApp: 'Abonelikler GymGM mobil uygulamasından satın alınır ve yönetilir.',
    openPlayStore: 'Google Play',
    openAppStore: 'App Store',
    programPreviewDays: 'gün',
    trainerViewProgram: 'Program',
    trainerViewNutrition: 'Beslenme',
    trainerViewProgress: 'İlerleme',
    back: 'Geri',
    noMeals: 'Öğün girilmemiş.',
    previewNotAvailable: 'Önizleme yok',
    currentProgram: 'Mevcut program',
    insightHistory: 'Geçmiş görüşler',
    snapshotHistory: 'Anlık görüntüler',
    landingTagline: 'Antrenmanın, beslenmen ve gelişimin — tek bir yerde.',
    landingSubtitle: 'Yapay zekâ ile hazırlanan antrenman programları, bir fotoğraf ya da cümleden öğün analizi, su takibi, seriler ve rozetler. Mobil uygulamayla aynı hesap.',
    landingFeatureProgramTitle: 'AI antrenman programı',
    landingFeatureProgramDesc: 'Hedefini, seviyeni ve günlerini seç — saniyeler içinde hazır program al, her seti ve tekrarı düzenle.',
    landingFeatureNutritionTitle: 'Beslenme AI',
    landingFeatureNutritionDesc: 'Öğününü yaz ya da fotoğrafını yükle; kalori, protein, karbonhidrat ve yağı anında gör.',
    landingFeatureStreakTitle: 'Seriler ve rozetler',
    landingFeatureStreakDesc: 'Antrenman ve beslenme serilerini koru, yol boyunca rozetlerin kilidini aç.',
    landingFeatureProgressTitle: 'Gelişim takibi',
    landingFeatureProgressDesc: 'Kilo ve yağ oranı grafikleri, haftalık AI özetleri ve program analizleri.',
    landingFeatureWaterTitle: 'Su takibi',
    landingFeatureWaterDesc: 'Tek dokunuşla su ekle, günlük hedefine ulaş.',
    landingFeatureTrainerTitle: 'Antrenör – sporcu bağlantısı',
    landingFeatureTrainerDesc: 'Antrenörler sporcularının programını, öğünlerini ve gelişimini tek panelden takip eder.',
    forgotPassword: 'Şifremi unuttum',
    sendResetLink: 'Sıfırlama bağlantısı gönder',
    resetLinkSent: 'Şifre sıfırlama e-postası gönderildi. Gelen kutunu kontrol et.',
    backToSignIn: 'Girişe dön',
    iAmA: 'Ben bir',
    blog: 'Blog',
    footerRights: 'Tüm hakları saklıdır.',
    starterTodosAllDone: "Tüm başlangıç görevlerini tamamladın; bu yüzden liste Ana Sayfa'da gösterilmiyor.",
    promoRedeemTitle: 'Kodun mu var?',
    promoRedeemDesc: 'Antrenöründen ya da bir GymGM kampanyasından aldığın kodu gir; Premium hem web\'de hem uygulamada açılsın.',
    promoCodePlaceholder: 'örn. EMRE10',
    promoApply: 'Uygula',
    promoAlreadyUsed: 'Bu kodu zaten kullandın.',
    promoAlreadyPremium: 'Mevcut planın bunu zaten içeriyor.',
    promoUsedUp: 'Bu kodun kullanım hakkı dolmuş.',
    promoError: 'Kod uygulanamadı. Lütfen tekrar dene.',
    adminPanel: 'Admin',
    adminPromoCodes: 'Promosyon kodları',
    adminCreateCode: 'Kod oluştur',
    adminCode: 'Kod',
    adminPlan: 'Plan',
    adminDurationDays: 'Premium süresi (gün)',
    adminMaxUses: 'Kullanım sınırı',
    adminLastRedeemDate: 'Son kullanım tarihi (isteğe bağlı)',
    adminNote: 'Not (örn. antrenör adı)',
    adminUses: 'Kullanım',
    adminActive: 'Aktif',
    adminInactive: 'Pasif',
    adminRedemptions: 'Kullananlar',
    adminNoCodes: 'Henüz kod yok.',
    adminNoRedemptions: 'Bu kodu henüz kimse kullanmadı.',
    adminCodeCreated: 'Kod oluşturuldu',
    adminErrExists: 'Bu isimde bir kod zaten var.',
    adminErrInvalidCode: 'Kod 3–32 karakter olmalı: harf, rakam, - veya _.',
    adminErrInvalidNumbers: 'Süreyi (1–3650 gün) ve kullanım sınırını (1–100000) kontrol et.',
    adminErrInvalidDate: 'Geçersiz tarih.',
    adminCopied: 'Kopyalandı',
    adminDeactivate: 'Pasifleştir',
    adminActivate: 'Aktifleştir',
    adminDays: 'gün',
    adminEndsAt: 'Premium bitişi',
    adminRedeemedAt: 'Kullanım tarihi',
    adminRefresh: 'Yenile',
    adminPressRefresh: 'Yüklemek için Yenile\'ye bas.',
    adminDelete: 'Sil',
    adminDeleteTitle: 'Kod silinsin mi?',
    adminDeleteMessage: 'Kod hemen çalışmaz hale gelir ve kullanım geçmişi silinir. Kodu daha önce kullananların Premium\'u bitiş tarihine kadar devam eder.',
    adminDeleted: 'Kod silindi',
    adminLegacy: 'Eski',
    adminFeedback: 'Geri bildirimler',
    adminFbStatus: 'Durum',
    adminFbStatusNew: 'Yeni',
    adminFbStatusRead: 'Okundu',
    adminFbStatusDone: 'Çözüldü',
    adminFbOpen: 'Açık',
    adminFbAll: 'Tümü',
    adminFbAllTypes: 'Tüm türler',
    adminFbAnonymous: 'Anonim',
    adminFbEmpty: 'Burada geri bildirim yok.',
    adminFbMarkAllRead: 'Tümünü okundu yap',
    adminFbDeleteTitle: 'Geri bildirim silinsin mi?',
    adminFbDeleted: 'Geri bildirim silindi',
    adminLeaderboard: 'XP sıralaması',
    adminLbTop: 'İlk kaç kişi',
    adminLbLevel: 'Seviye',
    adminLbEmpty: 'Henüz kimsenin XP\'si yok.',
    adminTrainers: 'Antrenörler',
    adminTrHint: 'Listesinde en az bir öğrencisi olan hesaplar. Öğrencilerini görmek için antrenöre tıkla.',
    adminTrSearch: 'Antrenör veya öğrenci ara',
    adminTrEmpty: 'Henüz öğrencisi bağlı antrenör yok.',
    adminTrNoMatch: 'Eşleşme yok.',
    adminTrCount: 'Öğrenci',
    adminTrNoTrainerEmail: 'Profilinde antrenör yok',
    adminTrNotTrainerRole: 'Rolü sporcuya çevrilmiş',
    adminRedeemHint: 'Kullanıcılar kodu gymgm.com → Ayarlar\'dan girer. Premium Android, iOS ve web\'de görünür.',
  },
};

// XP level titles (lakap), same as the app's xpTitleName.
const XP_TITLE_NAMES = {
  en: {
    founder: 'Founder',
    rookie: 'Rookie',
    committed: 'Committed',
    ironGrip: 'Iron Grip',
    discipline: 'Discipline Machine',
    gymRat: 'Gym Rat',
    steelWill: 'Steel Will',
    legend: 'Legend',
  },
  tr: {
    founder: 'Kurucu',
    rookie: 'Çaylak',
    committed: 'Kararlı',
    ironGrip: 'Demir Bilek',
    discipline: 'Disiplin Abidesi',
    gymRat: 'Salon Kurdu',
    steelWill: 'Çelik İrade',
    legend: 'Efsane',
  },
};

const BADGES = {
  en: {
    first_workout: ['First Step', 'Complete your first workout'],
    workout_5: ['Committed Start', 'Complete 5 workouts'],
    workout_25: ['Iron Will', 'Complete 25 workouts'],
    workout_50: ['Workout Master', 'Complete 50 workouts'],
    meal_10: ['Nutrition Awareness', 'Log 10 meals'],
    meal_50: ['Nutrition Tracker', 'Log 50 meals'],
    meal_100: ['Nutrition Expert', 'Log 100 meals'],
    perfect_week: ['Perfect Week', 'Finish every workout day in one week'],
    nutrition_streak_7: ['Steady Nutrition Streak', 'Log nutrition 7 days in a row'],
    body_progress_5: ['Body Tracker', 'Log your body measurements 5 times'],
    monthly_measurement: ['Monthly Check-In', 'Log your body measurements at least once a month'],
    water_week: ['Hydration Week', 'Hit your water goal 7 days in a row'],
    comeback: ['Comeback', 'Be active 3 days in a row after a break'],
    perfect_month: ['Perfect Month', 'Finish every workout day in one month'],
  },
  tr: {
    first_workout: ['İlk Adım', 'İlk antrenmanını tamamla'],
    workout_5: ['Azimli Başlangıç', '5 antrenman tamamla'],
    workout_25: ['Demir İrade', '25 antrenman tamamla'],
    workout_50: ['Antrenman Ustası', '50 antrenman tamamla'],
    meal_10: ['Beslenme Bilinci', '10 öğün gir'],
    meal_50: ['Beslenme Takipçisi', '50 öğün gir'],
    meal_100: ['Beslenme Uzmanı', '100 öğün gir'],
    perfect_week: ['Kusursuz Hafta', 'Haftalık programını eksiksiz tamamla'],
    nutrition_streak_7: ['Düzenli Beslenme Serisi', '7 gün üst üste beslenme gir'],
    body_progress_5: ['Vücut Takibi', 'Vücut ölçülerini 5 kez kaydet'],
    monthly_measurement: ['Aylık Ölçüm', 'Her ay en az bir kez vücut ölçünü kaydet'],
    water_week: ['Su Haftası', '7 gün üst üste su hedefini tuttur'],
    comeback: ['Geri Dönüş', 'Ara verdikten sonra 3 gün üst üste aktif ol'],
    perfect_month: ['Kusursuz Ay', 'Bir ayın tüm program günlerini tamamla'],
  },
};

const fmt = {
  en: {
    welcome: (name) => `Welcome, ${name}!`,
    allowanceTrainingRemaining: (n) => (n === 1 ? 'You have 1 AI program left this week' : `You have ${n} AI programs left this week`),
    allowanceNutritionRemaining: (n) => (n === 1 ? 'You have 1 meal analysis left today' : `You have ${n} meal analyses left today`),
    mealReviewAddItemButton: (n) => (n > 1 ? `Analyze ${n} foods` : 'Analyze'),
    streakFreezeSubtitle: (n) => `Missed yesterday · ${n} freezes left this month`,
    caloriesRemaining: (k) => `${k} kcal left`,
    caloriesOver: (k) => `${k} kcal over goal`,
    waterRemaining: (a) => `${a} to go`,
    waterGoalLine: (g) => `Goal ${g}`,
    waterDaysGoalMet: (d) => `${d}/7 days on goal`,
    macroTargetsDailyCalorieGoalLine: (c) => `Daily target: ${c} kcal`,
    programHistoryExerciseCountLabel: (n) => `${n} exercises`,
    traineeProgramName: (name) => `${name}'s Program`,
    ageValue: (a) => `Age: ${a}`,
    codeCount: (n) => `${n} code${n !== 1 ? 's' : ''}`,
    expiresIn: (time) => `Expires: ${time}`,
    badgeProgressLabel: (c, t) => `${c} / ${t}`,
    badgeNewlyEarnedMessage: (title) => `New badge earned: ${title} 🎉`,
    badgeEarnedAgainMessage: (title, n) => `Earned again: ${title} ×${n} 🎉`,
    badgeEarnedTimesLabel: (n) => `Earned ${n} times`,
    xpGained: (xp) => `+${xp} XP`,
    xpLevel: (level) => `Level ${level}`,
    xpLevelUpMessage: (level) => `Level up! You're now level ${level}.`,
    xpChestPrize: (prize) => `From the chest: ${prize}`,
    xpTitleUnlocked: (title) => `New title: ${title}`,
    xpToNext: (xp) => `${xp} XP to go`,
    xpProgress: (current, cost) => `${current} / ${cost} XP`,
    xpTitleUnlocksAt: (level) => `Unlocks at level ${level}`,
    dailySpinPrizeAdFree: (days) => `${days} days ad-free`,
    premiumUntil: (d) => `Premium until ${d}`,
    streakFreezeProgramTitle: (d) => `${d}-day workout streak at risk`,
    streakFreezeNutritionTitle: (d) => `${d}-day nutrition streak at risk`,
    programClearDayConfirm: (day) => `Remove all exercises from ${day}?`,
    programMakeRestDayConfirm: (day) => `${day} will become a rest day and its exercises will be removed.`,
    programCopiedToDays: (n) => (n === 1 ? 'Copied to 1 day' : `Copied to ${n} days`),
    programDaySummary: (n, min) => `${n === 1 ? '1 exercise' : `${n} exercises`} · ~${min} min`,
    programAddExercisesCta: (n) => (n === 1 ? 'Add 1 exercise' : `Add ${n} exercises`),
    programAddCustomExerciseNamed: (name) => `Add "${name}" as a custom exercise`,
    aiDaysPerWeek: (n) => `${n} days / week`,
    aiDaysPreset: (n) => `${n} days`,
    aiMinutes: (m) => `${m} min`,
    adminTrSummary: (trainers, trainees) => `${trainers} trainers · ${trainees} trainees`,
    adminTrOtherTrainer: (email) => `Trainer on their profile: ${email}`,
    aiPreviewSummary: (d, e) => `${d} days · ${e} exercises`,
    liftBestLabel: (set) => `Best: ${set}`,
    liftSessionCount: (n) => (n === 1 ? '1 workout' : `${n} workouts`),
  },
  tr: {
    premiumUntil: (d) => `${d} tarihine kadar Premium`,
    welcome: (name) => `Hoş geldin, ${name}!`,
    allowanceTrainingRemaining: (n) => `Bu hafta ${n} program oluşturma hakkın var`,
    allowanceNutritionRemaining: (n) => `Bugün ${n} öğün analizi hakkın var`,
    mealReviewAddItemButton: (n) => (n > 1 ? `${n} yemeği analiz et` : 'Analiz et'),
    streakFreezeSubtitle: (n) => `Dünü kaçırdın · Bu ay ${n} dondurma hakkın var`,
    caloriesRemaining: (k) => `${k} kcal kaldı`,
    caloriesOver: (k) => `Hedefi ${k} kcal aştın`,
    waterRemaining: (a) => `${a} kaldı`,
    waterGoalLine: (g) => `Hedef ${g}`,
    waterDaysGoalMet: (d) => `${d}/7 gün hedefte`,
    macroTargetsDailyCalorieGoalLine: (c) => `Günlük hedef: ${c} kcal`,
    programHistoryExerciseCountLabel: (n) => `${n} egzersiz`,
    traineeProgramName: (name) => `${name}'nin Programı`,
    ageValue: (a) => `Yaş: ${a}`,
    codeCount: (n) => `${n} kod`,
    expiresIn: (time) => `Bitiş: ${time}`,
    badgeProgressLabel: (c, t) => `${c} / ${t}`,
    badgeNewlyEarnedMessage: (title) => `Yeni rozet kazandın: ${title} 🎉`,
    badgeEarnedAgainMessage: (title, n) => `Rozeti yeniden kazandın: ${title} ×${n} 🎉`,
    badgeEarnedTimesLabel: (n) => `${n} kez kazanıldı`,
    xpGained: (xp) => `+${xp} XP`,
    xpLevel: (level) => `Seviye ${level}`,
    xpLevelUpMessage: (level) => `Seviye atladın! Artık seviye ${level}.`,
    xpChestPrize: (prize) => `Sandıktan çıktı: ${prize}`,
    xpTitleUnlocked: (title) => `Yeni lakap: ${title}`,
    xpToNext: (xp) => `${xp} XP kaldı`,
    xpProgress: (current, cost) => `${current} / ${cost} XP`,
    xpTitleUnlocksAt: (level) => `Seviye ${level} ile açılır`,
    dailySpinPrizeAdFree: (days) => `${days} gün reklamsız`,
    streakFreezeProgramTitle: (d) => `${d} günlük antrenman serin tehlikede`,
    streakFreezeNutritionTitle: (d) => `${d} günlük beslenme serin tehlikede`,
    programClearDayConfirm: (day) => `${day} günündeki tüm egzersizler silinsin mi?`,
    programMakeRestDayConfirm: (day) => `${day} dinlenme günü olacak ve egzersizleri silinecek.`,
    programCopiedToDays: (n) => `${n} güne kopyalandı`,
    programDaySummary: (n, min) => `${n} egzersiz · ~${min} dk`,
    programAddExercisesCta: (n) => `${n} egzersiz ekle`,
    programAddCustomExerciseNamed: (name) => `"${name}" adlı özel egzersizi ekle`,
    aiDaysPerWeek: (n) => `Haftada ${n} gün`,
    aiDaysPreset: (n) => `${n} gün`,
    aiMinutes: (m) => `${m} dk`,
    adminTrSummary: (trainers, trainees) => `${trainers} antrenör · ${trainees} öğrenci`,
    adminTrOtherTrainer: (email) => `Profilindeki antrenör: ${email}`,
    aiPreviewSummary: (d, e) => `${d} gün · ${e} egzersiz`,
    liftBestLabel: (set) => `En iyi: ${set}`,
    liftSessionCount: (n) => `${n} antrenman`,
  },
};

// exercises.json keeps English muscle / equipment values; these are display
// labels only (mirrors muscleGroupLabel / equipmentLabel in the Flutter app).
const MUSCLES_TR = {
  biceps: 'Ön kol',
  triceps: 'Arka kol',
  forearms: 'Bilek',
  calves: 'Baldır',
  cardio: 'Kardiyo',
  chest: 'Göğüs',
  core: 'Karın',
  'full body': 'Tüm vücut',
  glutes: 'Kalça',
  hamstrings: 'Arka bacak',
  legs: 'Bacak',
  'lower back': 'Bel',
  neck: 'Boyun',
  quadriceps: 'Ön bacak',
  shoulders: 'Omuz',
  traps: 'Trapez',
  'upper back': 'Sırt',
};

const EQUIPMENT = {
  en: { dumbell: 'Dumbbell', dumbbells: 'Dumbbell' },
  tr: {
    barbell: 'Bar',
    dumbbell: 'Dambıl',
    dumbbells: 'Dambıl',
    dumbell: 'Dambıl',
    bodyweight: 'Vücut ağırlığı',
    cable: 'Kablo',
    'cable machine': 'Kablo makinesi',
    rope: 'Halat',
    'leg press machine': 'Leg press makinesi',
    machine: 'Makine',
    'smith machine': 'Smith makinesi',
    'parallel bars': 'Paralel bar',
    plate: 'Plaka',
    'plyo box': 'Plyo kutusu',
    'pull-up bar': 'Barfiks barı',
    'swiss ball': 'Pilates topu',
  },
};

export function muscleLabel(muscle, lang) {
  if (!muscle) return '';
  return (lang === 'tr' && MUSCLES_TR[muscle.trim().toLowerCase()]) || muscle;
}

export function equipmentLabel(equipment, lang) {
  if (!equipment) return '';
  return EQUIPMENT[lang === 'tr' ? 'tr' : 'en'][equipment.trim().toLowerCase()] || equipment;
}

const DICT = { en: { ...en, ...extra.en }, tr: { ...tr, ...extra.tr } };

export const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/** Uppercase that respects Turkish dotted/dotless i (mirrors toUpperCaseForDisplay). */
export function upper(text, lang) {
  if (!text) return '';
  return lang === 'tr' ? text.toLocaleUpperCase('tr-TR') : text.toUpperCase();
}

export function translate(lang, key, ...args) {
  const l = DICT[lang] ? lang : 'en';
  const f = fmt[l][key];
  if (f) return f(...args);
  return DICT[l][key] ?? DICT.en[key] ?? key;
}

export function useT() {
  const { language } = useLanguage();
  const lang = language === 'tr' ? 'tr' : 'en';
  const t = useCallback((key, ...args) => translate(lang, key, ...args), [lang]);
  t.lang = lang;
  t.upper = (s) => upper(s, lang);
  t.day = (dayKey) => translate(lang, dayKey);
  t.dayShort = (dayKey) => translate(lang, `${dayKey}Short`);
  t.muscle = (m) => muscleLabel(m, lang);
  t.equipment = (e) => equipmentLabel(e, lang);
  t.badgeTitle = (id) => (BADGES[lang][id] || [id])[0];
  t.badgeDescription = (id) => (BADGES[lang][id] || ['', ''])[1];
  t.xpTitle = (id) => XP_TITLE_NAMES[lang][id] || id;
  t.locale = lang === 'tr' ? 'tr-TR' : 'en-US';
  return t;
}
